// The reseller's Domain pricing list: a card with a search box, a status filter and a pricing
// filter, then a table of the TLDs KilatDomain offers, in the reseller's order — the TLD, its
// Pricing dialog, whether the reseller sells it, and the buttons that order it. The search and
// the filters are the route's search params (guide, rule 16): this draws them and hands up what
// changed; arrangeTlds applies them. The route cuts the page and hands in the caption and the
// pagination. A reseller not active yet reads it (canManage, from the route); every role of an
// active one sees the controls, and the API refuses billing and readonly (rule 14).
import type { ResellerTld } from "@kilat/contracts/reseller-pricing";
import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { m } from "@/lib/paraglide/messages.js";
import { MoveTldButtons } from "./move-tld/buttons";
import { TldEnabledSwitch } from "./set-tld-enabled/switch";
import { SetTldPriceDialog } from "./set-tld-price/dialog";

export const STATUS_FILTERS = ["all", "enabled", "disabled"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];
export const PRICED_FILTERS = ["all", "priced", "unpriced"] as const;
export type PricedFilter = (typeof PRICED_FILTERS)[number];

export type PricingSearch = { q: string; status: StatusFilter; priced: PricedFilter };

/** What the list shows with nothing in the URL: every offered TLD. */
export const DEFAULT_PRICING_SEARCH: PricingSearch = { q: "", status: "all", priced: "all" };

/** A TLD is priced once the reseller sells some action of some term. */
const isPriced = (tld: ResellerTld) =>
  tld.terms.some(({ retail }) => retail.register !== null || retail.transfer !== null || retail.renew !== null);

/** The TLDs that match the search (any part of the TLD, its dot optional) and the filters, in list order. */
export function arrangeTlds(tlds: readonly ResellerTld[], { q, status, priced }: PricingSearch): ResellerTld[] {
  const needle = q.trim().toLowerCase().replace(/^\./, "");
  return tlds.filter(
    (tld) =>
      tld.tld.includes(needle) &&
      (status === "all" || tld.enabled === (status === "enabled")) &&
      (priced === "all" || isPriced(tld) === (priced === "priced")),
  );
}

const isFiltered = (search: PricingSearch) =>
  search.q.trim() !== "" ||
  search.status !== DEFAULT_PRICING_SEARCH.status ||
  search.priced !== DEFAULT_PRICING_SEARCH.priced;

type SearchProps = { search: PricingSearch; onSearchChange: (next: Partial<PricingSearch>) => void };

const STATUS_LABELS: Record<StatusFilter, () => string> = {
  all: m.reseller_pricing_status_all,
  enabled: m.reseller_pricing_status_enabled,
  disabled: m.reseller_pricing_status_disabled,
};

const PRICED_LABELS: Record<PricedFilter, () => string> = {
  all: m.tld_pricing_priced_all,
  priced: m.tld_pricing_priced_yes,
  unpriced: m.tld_pricing_priced_no,
};

/** A filter's Select: its items drawn as options, the choice handed up. */
function Filter<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <Select items={items} value={value} onValueChange={(next: T | null) => next && onChange(next)}>
      <SelectTrigger aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Toolbar({ search, onSearchChange }: SearchProps) {
  const statuses = STATUS_FILTERS.map((value) => ({ value, label: STATUS_LABELS[value]() }));
  const priced = PRICED_FILTERS.map((value) => ({ value, label: PRICED_LABELS[value]() }));
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {/* Uncontrolled: the URL is the state, and each keystroke is handed up to it. */}
      <Input
        type="search"
        className="sm:max-w-xs"
        defaultValue={search.q}
        placeholder={m.tld_pricing_search()}
        aria-label={m.tld_pricing_search()}
        onChange={(event) => onSearchChange({ q: event.target.value })}
      />
      <Filter
        label={m.reseller_pricing_status_filter()}
        items={statuses}
        value={search.status}
        onChange={(status) => onSearchChange({ status })}
      />
      <Filter
        label={m.tld_pricing_priced_filter()}
        items={priced}
        value={search.priced}
        onChange={(next) => onSearchChange({ priced: next })}
      />
    </div>
  );
}

export function PricingTable({
  tlds,
  all,
  canManage,
  search,
  onSearchChange,
  caption,
  pagination,
}: SearchProps & {
  /** The rows to draw: the page of the arranged list. */
  tlds: readonly ResellerTld[];
  /** The whole list, in order: what "first" and "last" mean to the move buttons. */
  all: readonly ResellerTld[];
  /** False while the reseller is pending or suspended: the API refuses every change. */
  canManage: boolean;
  caption?: string;
  pagination?: ReactNode;
}) {
  // Moving a TLD swaps it with its neighbour in the whole list, which a search or filter may hide.
  const filtered = isFiltered(search);
  const orderable = canManage && !filtered;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.tld_pricing_list_title()}</CardTitle>
        {canManage && filtered && <CardDescription>{m.tld_pricing_order_filtered()}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {all.length > 0 && <Toolbar search={search} onSearchChange={onSearchChange} />}
        {all.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m.reseller_pricing_empty_title()}</EmptyTitle>
              <EmptyDescription>{m.reseller_pricing_empty_description()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : tlds.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m.tld_pricing_no_match_title()}</EmptyTitle>
              <EmptyDescription>{m.tld_pricing_no_match_body()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table>
            {caption && <TableCaption className="text-left">{caption}</TableCaption>}
            <TableHeader>
              <TableRow>
                <TableHead>{m.tld_pricing_column_tld()}</TableHead>
                <TableHead />
                <TableHead>{m.reseller_pricing_column_enabled()}</TableHead>
                {canManage && <TableHead>{m.tld_pricing_column_actions()}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tlds.map((tld) => (
                <TableRow key={tld.id}>
                  <TableCell>.{tld.tld}</TableCell>
                  <TableCell>
                    <SetTldPriceDialog tld={tld} editable={canManage} />
                  </TableCell>
                  <TableCell>
                    <TldEnabledSwitch tld={tld} editable={canManage} />
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      {orderable && (
                        <div className="flex items-center gap-1">
                          <MoveTldButtons
                            tldId={tld.id}
                            tld={tld.tld}
                            first={all[0]?.id === tld.id}
                            last={all.at(-1)?.id === tld.id}
                          />
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {pagination}
      </CardContent>
    </Card>
  );
}
